import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { z } from "zod";
import { KeyRound } from "lucide-react";
import { api, ApiError } from "@/lib/api";

const searchSchema = z.object({
  token: z.string().min(1),
});

export const Route = createFileRoute("/reset-password")({
  component: ResetPasswordPage,
  validateSearch: searchSchema,
});

function ResetPasswordPage() {
  const { token } = Route.useSearch();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);

  const reset = useMutation({
    mutationFn: () => api.post("/auth/reset-password", { token, password }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["auth", "me"] });
      navigate({ to: "/" });
    },
    onError: (err) => {
      const detail =
        err instanceof ApiError ? JSON.stringify(err.payload) : String(err);
      setError(detail);
    },
  });

  const passwordOk = password.length >= 8 && password === confirm;

  return (
    <div className="min-h-screen grid place-items-center bg-base-200 p-4">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          setError(null);
          reset.mutate();
        }}
        className="card-elevated w-full max-w-sm p-6 space-y-4"
      >
        <div>
          <div className="text-xs font-semibold uppercase tracking-[0.18em] text-primary">
            Axorial · Portal
          </div>
          <h1 className="text-2xl font-display font-semibold mt-1">Nueva contraseña</h1>
          <p className="text-sm text-base-content/65 mt-1">
            Definí una nueva contraseña para tu cuenta. Mínimo 8 caracteres.
          </p>
        </div>

        <label className="form-control w-full">
          <span className="label-text text-xs font-semibold uppercase tracking-wider text-base-content/60 mb-1">
            Nueva contraseña
          </span>
          <input
            type="password"
            className="input input-bordered w-full"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="new-password"
            autoFocus
            required
            minLength={8}
          />
        </label>
        <label className="form-control w-full">
          <span className="label-text text-xs font-semibold uppercase tracking-wider text-base-content/60 mb-1">
            Confirmar
          </span>
          <input
            type="password"
            className="input input-bordered w-full"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            required
          />
        </label>

        {error && (
          <div className="text-sm text-error border border-error/30 bg-error/5 rounded-md p-2">
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={reset.isPending || !passwordOk}
          className="btn btn-primary w-full"
        >
          {reset.isPending && <span className="loading loading-spinner loading-xs" />}
          <KeyRound className="size-4" />
          Guardar nueva contraseña
        </button>
      </form>
    </div>
  );
}
